import React from 'react';
import styled from '@emotion/styled';
import { withTheme, Theme } from '@emotion/react';
import { ThemeProps } from '@theme/base';
import { mq } from '@styles/breakpoints';
import { TourTestimonial } from '@model/iceberg/testimonial';
import { TripTypes } from '@model/common';
import { Icons } from '@model/common/icons';
import { TestId } from '@components/test-ids';
import { useI18NextContext } from '@components/hooks';
import { Button, ButtonVariant, ButtonColor, ButtonSize } from '@components/material-ui';
import { TourLink } from '@components/common/next-link/tour-link';
import { Icon } from '@components/common/generic-icon';
import { StarRating } from '@components/common/star-rating';

export interface TourReviewProps {
  theme: Theme;
  testimonial: TourTestimonial;
  destinationName: string;
  tripType: TripTypes;
}

const Container = styled.div(({ theme }: ThemeProps) => ({
  display: 'flex',
  flexDirection: 'column',
  padding: theme.custom.spacing.medium,
  [mq.small]: {
    padding: theme.custom.spacing.large
  }
}));

const Headline = styled.div(({ theme }: ThemeProps) => ({
  marginTop: theme.custom.spacing.medium,
  marginBottom: 0,
  [mq.small]: {
    marginTop: theme.custom.spacing.large
  },
  color: theme.custom.colors.white,
  ...(theme.custom.typography.h4 as any)
}));

const SubHeading = styled.div(({ theme }: ThemeProps) => ({
  ...(theme.custom.typography.h5 as any),
  color: theme.custom.colors.white,
  marginBottom: theme.custom.spacing.large
}));

const IconContainer = styled.div(({ theme }: ThemeProps) => ({
  opacity: 0.2,
  color: theme.custom.colors.white,
  i: {
    color: theme.custom.colors.white
  }
}));

const ReviewContainer = styled.p(({ theme }: ThemeProps) => ({
  color: theme.custom.colors.white,
  marginTop: theme.custom.spacing.xSmall,
  marginBottom: theme.custom.spacing.xSmall,
  [mq.small]: {
    marginTop: theme.custom.spacing.large,
    marginBottom: theme.custom.spacing.large
  },
  ...(theme.custom.typography.paragraph as any)
}));

const Author = styled.div(({ theme }: ThemeProps) => ({
  ...(theme.custom.typography.paragraph as any),
  color: theme.custom.colors.white,
  fontWeight: 'bold',
  margin: 0
}));

const TourName = styled.div(({ theme }: ThemeProps) => ({
  ...(theme.custom.typography.paragraph as any),
  color: theme.custom.colors.white
}));

const ArrowIcon = styled(Icon)(({ theme }: ThemeProps) => ({
  fontSize: '1.6rem',
  color: theme.custom.colors.white
}));

const StyledButton: any = styled(Button)(({ theme }: ThemeProps) => {
  return {
    marginTop: theme.custom.spacing.large,
    ['.MuiButtonBase-root']: {
      borderRadius: 10,
      color: theme.custom.colors.white,
      padding: theme.custom.spacing.large,
      width: 'unset',
      ['&:hover']: {
        backgroundColor: theme.custom.states.buttons.primary.default
      }
    }
  };
});

export const TourReviewComponent = ({ theme, testimonial, destinationName, tripType }: TourReviewProps) => {
  const t: any = useI18NextContext();

  if (!testimonial) return null;

  const { name, review, tour } = testimonial;
  const tripTypeLabel = t(`trip-types__${tripType}--singular`);

  return (
    <Container data-testid={TestId.testimonial.review}>
      <StarRating rating={5} size={16} containerStyling={{ marginTop: theme.custom.spacing.small }} />
      <Headline>{t('tours__testimonials--header', { destination: destinationName, tripType: tripTypeLabel })}</Headline>
      <SubHeading>{t('tours__testimonials--subheader')}</SubHeading>
      <IconContainer>
        <Icon name={'mhi-quote'} css={{ fontSize: '4rem' }} />
      </IconContainer>
      <ReviewContainer>{review}</ReviewContainer>
      <Author>{name}</Author>
      <TourName>{tour.name.display}</TourName>
      <TourLink path={tour.name.path} tripType={tripType}>
        <StyledButton
          variant={ButtonVariant.OUTLINED}
          label={t('tours__testimonials--link')}
          color={ButtonColor.SECONDARY}
          size={ButtonSize.SMALL}
          endIcon={<ArrowIcon name={Icons.ARROW_RIGHT} />}
        />
      </TourLink>
    </Container>
  );
};

export const TourReview = withTheme(TourReviewComponent);
